import kebabCase from "lodash/kebabCase";
import React, { useMemo } from "react";

import Big from "big.js";
import { DataTable, useDataTable, createColumnHelper, Text } from "@puzzle/ui";

import { sumAmounts, formatMoney, capitalizeName } from "@puzzle/utils";
import { PayrollSummaryItemFragment } from "./graphql.generated";
import { Box } from "@puzzle/ve";
import { S, vars } from "@puzzle/theme";

type PayrollSummaryRow = {
  itemName: string;
  amount: string;
  isSplit?: boolean;
} & {
  splits: PayrollSummaryRow[];
};

const columnHelper = createColumnHelper<PayrollSummaryRow>();

const indent = (
  <svg width="7" height="7" viewBox="0 0 7 7" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path opacity="0.4" d="M1 0.0200195V6.02002H7" stroke={vars.colors["text-neutral-highlight"]} />
  </svg>
);

export const SummaryTable = ({ summaryItems }: { summaryItems: PayrollSummaryItemFragment[] }) => {
  const totalAmount = useMemo(
    () => Big(sumAmounts(summaryItems.map(({ amount }) => amount ?? "0"))),
    [summaryItems]
  );

  const columns = useMemo(() => {
    return [
      columnHelper.accessor("itemName", {
        header: "Item",
        cell: ({ row, getValue }) => {
          const value = capitalizeName(getValue());

          if (row.original.isSplit)
            return (
              <Box css={{ marginLeft: S["0h"] }}>
                <Text color="text-neutral-subdue" size="bodyXS" css={{ marginRight: "$1" }}>
                  {indent}
                </Text>
                {value}
              </Box>
            );

          return value;
        },
        footer: () => <>Total payable</>,
      }),
      columnHelper.accessor("amount", {
        header: "Amount",
        size: 50,
        meta: {
          align: "right",
        },
        cell: ({ row, getValue }) => {
          const amount = Big(getValue() ?? "0").abs();
          const displayAmount = !amount.eq(0) ? formatMoney({ currency: "USD", amount }) : "-";
          const itemName = kebabCase(row.original.itemName);
          return <div data-testid={`${itemName}-amount`}>{displayAmount}</div>;
        },
        footer: () => {
          return (
            <div data-testid="total-payable-amount">
              {formatMoney({ currency: "USD", amount: totalAmount })}
            </div>
          );
        },
      }),
    ];
  }, [totalAmount]);

  const data = useMemo(() => {
    return summaryItems.map(({ itemName, amount, itemSplits }) => {
      const item = { itemName, amount };

      return {
        ...item,
        splits: itemSplits?.map(({ name, amount }) => {
          return {
            amount,
            itemName: name,
            isSplit: true,
          };
        }),
      };
    }) as PayrollSummaryRow[];
  }, [summaryItems]);

  const table = useDataTable({
    data,
    columns,
    getSubRows: ({ splits }) => splits,
    state: {
      expanded: true,
    },
  });

  return (
    <DataTable
      table={table}
      bordered
      hoverableRows={false}
      getRowProps={({ original }) => {
        if (!original.isSplit) {
          return { style: { backgroundColor: vars.colors["surface-neutral-highlight"] } };
        }

        return {};
      }}
    />
  );
};
